import {
  BlotterTableContext,
  EMPTY_OBJECT,
  useAggregationsContext,
  useCurrenciesContext,
  useExposureDefinitions,
  useIntl,
  useMarketAccountsContext,
  useMarketsContext,
  useObservableValue,
  useSecuritiesContext,
  useStrategiesContext,
  useUserContext,
  type BlotterTableContextProps,
} from '@talos/kyoko';
import { useMemo, type PropsWithChildren } from 'react';
import { useCustomersByName, useCustomersContext } from '../hooks/useCustomer';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useGetCounterpartiesQuery } from './AppStateProvider/streamingDataSlice';
import { useFixingIndices } from './FixingIndexProvider';
import { useOptionalPredictionsContext } from './PredictionsProvider';
import { useSubAccounts } from './SubAccountsContext';
import { useTradingSettings } from './TradingSettingsContext';
import { useUsersContext } from './UsersContext';

const EMPTY_PREDICTION_EVENTS_BY_ID: NonNullable<BlotterTableContextProps['predictionEventsByID']> = new Map();
const EMPTY_PREDICTION_EVENT_SERIES_BY_ID: NonNullable<BlotterTableContextProps['predictionEventSeriesByID']> =
  new Map();

export function BlotterTableContextProvider({ children }: PropsWithChildren) {
  const { users, userByID } = useUsersContext();
  const { user: currentUser } = useUserContext();
  const intl = useIntl();

  const { strategiesByName } = useStrategiesContext();

  const { currenciesBySymbol } = useCurrenciesContext();
  const { securitiesBySymbol } = useSecuritiesContext();
  const predictions = useOptionalPredictionsContext();
  const { marketDisplayNameByName, marketTypeByName } = useMarketsContext();
  const { customers: customersObs } = useCustomersContext();
  const customers = useObservableValue(() => customersObs, [customersObs], []);
  const customersByName = useCustomersByName();
  const { marketAccountDisplayNameByName, marketAccountsByName, marketAccountsByID } = useMarketAccountsContext();
  const { subAccountsByName, allSubAccounts, subAccountsByID } = useSubAccounts();
  const { exposureDefinitionsByName } = useExposureDefinitions();
  const { aggregationsByName } = useAggregationsContext();
  const { data: counterpartiesByName } = useGetCounterpartiesQuery(EMPTY_OBJECT);
  const { exportDatesRaw, showAllDecimalsInBlotters } = useTradingSettings();
  const { disableBlotterErrorBanner, enableSEDOLDisplay } = useFeatureFlag();
  const { fixingIndicesByName } = useFixingIndices();

  const context = useMemo(
    () =>
      ({
        currentUser,
        users,
        userByID,
        marketDisplayNameByName,
        marketTypeByName,
        strategiesByName,
        currenciesBySymbol,
        securitiesBySymbol,
        predictionEventsByID: predictions?.eventsByID ?? EMPTY_PREDICTION_EVENTS_BY_ID,
        predictionEventSeriesByID: predictions?.eventSeriesByID ?? EMPTY_PREDICTION_EVENT_SERIES_BY_ID,
        customers,
        customersByName,
        counterpartiesByName,
        marketAccountDisplayNameByName,
        marketAccountsByName,
        marketAccountsByID,
        subAccountsByName,
        subAccountsByID,
        allSubAccounts,
        exposureDefinitionsByName,
        aggregationsByName,
        fixingIndicesByName,
        intl,
        settings: {
          exportDatesRaw,
          disableErrorBanner: disableBlotterErrorBanner,
          showAllDecimals: showAllDecimalsInBlotters,
          includeSedolInSecuritySearch: enableSEDOLDisplay,
        },
      }) satisfies BlotterTableContextProps,
    [
      currentUser,
      users,
      userByID,
      marketDisplayNameByName,
      marketTypeByName,
      strategiesByName,
      currenciesBySymbol,
      securitiesBySymbol,
      predictions?.eventsByID,
      predictions?.eventSeriesByID,
      customers,
      customersByName,
      counterpartiesByName,
      marketAccountDisplayNameByName,
      marketAccountsByName,
      marketAccountsByID,
      subAccountsByName,
      subAccountsByID,
      allSubAccounts,
      exposureDefinitionsByName,
      aggregationsByName,
      fixingIndicesByName,
      intl,
      exportDatesRaw,
      disableBlotterErrorBanner,
      enableSEDOLDisplay,
      showAllDecimalsInBlotters,
    ]
  );

  return <BlotterTableContext.Provider value={context}>{children}</BlotterTableContext.Provider>;
}
